"use client"

import type { ReactNode } from "react"

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react"

import { tv, type VariantProps } from "@/lib/utils/tv"

const tabs = tv({
  slots: {
    list: "inline-flex",
    tab: "font-display focus-visible:ring-brand transition-colors focus-visible:ring-2 focus-visible:outline-none",
    count: "",
  },
  variants: {
    variant: {
      underline: {
        list: "border-line-subtle gap-100 border-b",
        tab: "text-body text-fg-subtle data-selected:border-brand data-selected:text-fg-brand -mb-px border-b-2 border-transparent px-400 py-250 font-semibold focus-visible:ring-inset",
        count: "text-fg-muted ml-100 font-normal",
      },
      pill: {
        list: "gap-250",
        tab: "hit-target text-body text-fg bg-surface shadow-sm data-selected:bg-brand data-selected:text-fg-inverted inline-flex h-8.5 items-center gap-150 rounded-full px-300 font-medium @2xl:h-9",
        count: "text-body-sm inline-flex h-5 min-w-8 items-center justify-center rounded-full border border-current px-150 leading-none font-semibold tabular-nums",
      },
    },
  },
  defaultVariants: { variant: "underline" },
})

interface TabsProps extends VariantProps<typeof tabs> {
  tabs: { label: string; count?: number; panel?: ReactNode }[]
  selectedIndex?: number
  onChange?: (index: number) => void
}

export const Tabs = ({ tabs: items, variant, selectedIndex, onChange }: TabsProps) => {
  const styles = tabs({ variant })
  return (
    <TabGroup selectedIndex={selectedIndex} onChange={onChange}>
      <TabList className={styles.list()}>
        {items.map((tab) => (
          <Tab key={tab.label} className={styles.tab()}>
            {tab.label}
            {tab.count !== undefined && <span className={styles.count()}>{tab.count}</span>}
          </Tab>
        ))}
      </TabList>
      {items.some((tab) => tab.panel) && (
        <TabPanels className="pt-400">
          {items.map((tab) => (
            <TabPanel key={tab.label}>{tab.panel}</TabPanel>
          ))}
        </TabPanels>
      )}
    </TabGroup>
  )
}
